import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import * as api from "../api/client";
import ChartWidget from "./ChartWidget";
import KpiWidget from "./KpiWidget";
import TableWidget from "./TableWidget";
import { currentModelSelection } from "./ModelPicker";

// The dashboard's widget builder. Three ways to define the data — Build (pick a table, group by, measures
// written as formulas), SQL, or Ask AI (the model writes the SQL) — then Run shows the result, the user
// picks how to show it, and Save puts it on the dashboard. The SAVED thing is the query, not the numbers:
// every refresh re-runs it (backend services/query_builder.py), so the widget always shows live data.

const BUCKETS = ["", "day", "week", "month", "quarter", "year"];
const OPS = [
  ["eq", "="], ["ne", "≠"], ["gt", ">"], ["gte", "≥"], ["lt", "<"], ["lte", "≤"],
  ["between", "between"], ["contains", "contains"], ["in", "is one of"], ["nin", "is not one of"],
  ["blank", "is blank"], ["present", "has a value"],
];
const NO_VALUE_OPS = new Set(["blank", "present"]);
const TYPES = [["bar", "Bar"], ["line", "Line"], ["pie", "Pie"], ["table", "Table"], ["kpi", "KPI card"]];
const INSERTS = ["+", "-", "*", "/", "(", ")", "SUM()", "AVG()", "COUNT()", "COUNT_DISTINCT()", "MIN()", "MAX()",
  "IF(, , )", "ROUND(, 2)"];

// Joined tables are B, C, … in formulas ([B.Receipt Collected]); the main table is A and needs no prefix.
const ALIASES = "BCDEFGH";
const JOIN_TYPES = [
  ["left", "keep every main-table row"],
  ["inner", "only rows that match"],
  ["full", "keep rows of both tables"],
];

const emptyBuild = (dataset = "") => ({
  dataset,
  joins: [],
  dimensions: [{ field: "", bucket: "" }],
  measures: [{ label: "", formula: "" }],
  calculated: [],
  filters: [],
  conditions: [],
  date_field: "",
  sort: { by: "", desc: true },
  limit: 100,
});

/** The builder's filter rows -> the {column: {op: value}} filters the query tools take. */
function filtersToSpec(rows) {
  const out = {};
  for (const r of rows || []) {
    if (!r.field || !r.op) continue;
    let v = r.value;
    if (NO_VALUE_OPS.has(r.op)) v = true;
    else if (r.op === "between") v = [r.value, r.value2];
    else if (r.op === "in" || r.op === "nin") v = String(r.value || "").split(",").map((s) => s.trim()).filter(Boolean);
    else if (v === "" || v === undefined) continue;
    out[r.field] = { ...(out[r.field] || {}), [r.op]: v };
  }
  return out;
}

function specToFilters(spec) {
  const rows = [];
  for (const [field, ops] of Object.entries(spec || {})) {
    for (const [op, v] of Object.entries(ops || {})) {
      if (op === "between") rows.push({ field, op, value: v?.[0] ?? "", value2: v?.[1] ?? "" });
      else if (op === "in" || op === "nin") rows.push({ field, op, value: (v || []).join(", ") });
      else rows.push({ field, op, value: NO_VALUE_OPS.has(op) ? "" : v });
    }
  }
  return rows;
}

/** Client mirror of query_builder.to_widget, for the live preview (the server builds the saved one). */
function toWidget(result, view, title) {
  if (!result) return null;
  const rows = result.rows || [];
  const cols = result.columns || [];
  if (view.type === "table") return { kind: "table", spec: { title, columns: cols, rows, matched_rows: rows.length, returned: rows.length } };
  const ys = (view.y || []).filter((y) => cols.includes(y));
  if (!ys.length) return null;
  if (view.type === "kpi") {
    const first = rows[0] || {};
    return { kind: "kpi", spec: { title, cards: ys.map((y) => ({ label: y, value: typeof first[y] === "number" ? first[y] : null, unit: view.units?.[y] || "" })) } };
  }
  if (!cols.includes(view.x)) return null;
  const pts = rows.slice(0, 500);
  if (view.split && cols.includes(view.split) && view.type !== "pie") {
    const labels = [...new Set(pts.map((r) => String(r[view.x])))];
    const names = [...new Set(pts.map((r) => String(r[view.split])))].slice(0, 12);
    const grid = new Map(pts.map((r) => [`${r[view.x]}\u0000${r[view.split]}`, r[ys[0]]]));
    return { kind: "chart", spec: { type: view.type, title, labels, series: names.map((n) => ({ name: n, data: labels.map((l) => grid.get(`${l}\u0000${n}`) ?? null)})) } };
  }
  let series = ys.map((y) => ({ name: y, data: pts.map((r) => (typeof r[y] === "number" ? r[y] : null)) }));
  if (view.type === "pie") series = series.slice(0, 1);
  return { kind: "chart", spec: { type: view.type, title, labels: pts.map((r) => String(r[view.x])), series } };
}

function Preview({ widget }) {
  if (!widget) return <div className="wb-empty">Choose the columns to show.</div>;
  if (widget.kind === "kpi") return <KpiWidget spec={widget.spec} />;
  if (widget.kind === "table") return <TableWidget spec={widget.spec} />;
  return <ChartWidget spec={widget.spec} />;
}

export default function WidgetBuilderModal({ dashboardId, editing, dateRange, onSaved, onClose }) {
  const src = editing?.spec?._source?.args;
  const [tab, setTab] = useState(src?.query?.mode || "build");
  const [datasets, setDatasets] = useState(null);
  const [build, setBuild] = useState(() => {
    const b = src?.query?.mode === "build" ? src.query.build : null;
    return b ? { ...emptyBuild(b.dataset), ...b, filters: specToFilters(b.filters) } : emptyBuild();
  });
  const [sql, setSql] = useState(src?.query?.sql || "");
  const [prompt, setPrompt] = useState(src?.query?.prompt || "");
  const [title, setTitle] = useState(editing?.title || src?.title || "");
  const [view, setView] = useState(src?.view || { type: "bar", x: "", y: [], split: "", units: {} });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(""); // "", "run", "generate", "save"
  const [error, setError] = useState("");
  const focused = useRef(null); // {kind, index, el} of the formula input last focused, for inserts
  const [hovered, setHovered] = useState(null); // the column under the mouse in the Columns list

  useEffect(() => {
    let cancelled = false;
    api.builderDatasets()
      .then((d) => {
        if (cancelled) return;
        setDatasets(d);
        setBuild((b) => (b.dataset ? b : { ...b, dataset: d[0]?.key || "" }));
      })
      .catch((e) => !cancelled && setError(e.message));
    return () => { cancelled = true; };
  }, []);

  const ds = useMemo(() => (datasets || []).find((d) => d.key === build.dataset), [datasets, build.dataset]);
  const findDs = (key) => (datasets || []).find((d) => d.key === key);
  // Every column the formulas can use: the main table's as "Column", each joined table's as "B.Column".
  const tables = useMemo(() => [
    { alias: "A", ds },
    ...(build.joins || []).map((j, i) => ({ alias: ALIASES[i], ds: (datasets || []).find((d) => d.key === j.dataset) })),
  ].filter((t) => t.ds), [ds, build.joins, datasets]);
  const allCols = useMemo(() => tables.flatMap((t) => (t.ds.columns || []).map((c) => ({
    ...c, alias: t.alias, ref: t.alias === "A" ? c.name : `${t.alias}.${c.name}`,
  }))), [tables]);
  const colNames = allCols.map((c) => c.ref);
  const dateCols = allCols.filter((c) => c.kind === "date").map((c) => c.ref);
  const mainDateCols = (ds?.columns || []).filter((c) => c.kind === "date").map((c) => c.name);
  const calcNames = (build.calculated || []).map((c) => c.name).filter(Boolean);
  // What each column means, from the Table Catalog — shown on hover so users pick the right one.
  const colMeta = useMemo(() => new Map(allCols.map((c) => [c.ref, c])), [allCols]);
  const describe = useMemo(() => new Map(allCols.map((c) => [c.ref, c.description || ""])), [allCols]);
  const tip = (ref) => {
    const c = colMeta.get(ref);
    const head = c ? `${ref} (${c.kind})` : ref;
    return [head, c?.description || "No description in the Table Catalog yet."].join("\n");
  };
  const colOption = (c) => <option key={c} value={c} title={describe.get(c) || ""}>{c}</option>;

  const query = () => {
    if (tab === "build") return { mode: "build", build: { ...build, filters: filtersToSpec(build.filters) } };
    return { mode: tab === "prompt" ? "prompt" : "sql", sql, prompt: tab === "prompt" ? prompt : undefined };
  };

  async function run() {
    setBusy("run");
    setError("");
    try {
      const r = await api.builderPreview(query(), dateRange || {});
      setResult(r);
      // Keep the user's view if it still fits the new columns. Otherwise take the suggested columns, but
      // keep the chart type the user chose (an edit that renames a measure must not turn a bar into a line)
      // — only the very first run of a new widget takes the suggested type too.
      const firstRun = !result && !editing;
      setView((v) => {
        const fits = v.type === "table" || ((v.y || []).length && (v.y || []).every((y) => r.columns.includes(y)) &&
          (v.type === "kpi" || r.columns.includes(v.x)));
        if (fits) return v;
        return { units: {}, ...r.suggestion, type: firstRun ? r.suggestion.type : v.type };
      });
    } catch (e) {
      setResult(null);
      setError(e.message);
    } finally {
      setBusy("");
    }
  }

  async function generate() {
    setBusy("generate");
    setError("");
    try {
      const r = await api.builderGenerate(prompt, currentModelSelection());
      setSql(r.sql || "");
      if (r.error) setError(`The generated SQL still fails: ${r.error}. Edit it below and press Run.`);
      else if (!title) setTitle(prompt.slice(0, 80));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  }

  async function save() {
    setBusy("save");
    setError("");
    try {
      const res = await api.saveCustomWidget(dashboardId, { title: title || "Custom widget", query: query(), view }, editing?.id || null);
      onSaved(res.widgets);
    } catch (e) {
      setError(e.message);
      setBusy("");
    }
  }

  const set = (patch) => setBuild((b) => ({ ...b, ...patch }));
  const setRow = (key, i, patch) => set({ [key]: build[key].map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const addRow = (key, row) => set({ [key]: [...(build[key] || []), row] });
  const dropRow = (key, i) => set({ [key]: build[key].filter((_, j) => j !== i) });

  /** Put text at the caret of the formula field the user was last in (or the first measure). */
  function insert(snippet) {
    const f = focused.current || { kind: "measures", index: 0, key: "formula" };
    const rows = build[f.kind] || [];
    if (!rows[f.index]) return;
    const cur = rows[f.index][f.key] || "";
    const el = f.el;
    const at = el && typeof el.selectionStart === "number" ? el.selectionStart : cur.length;
    const next = cur.slice(0, at) + snippet + cur.slice(at);
    setRow(f.kind, f.index, { [f.key]: next });
    // Caret inside the parentheses for a function, after the text otherwise.
    const caret = at + (snippet.endsWith("()") ? snippet.length - 1 : snippet.startsWith("IF(") || snippet.startsWith("ROUND(") ? snippet.indexOf("(") + 1 : snippet.length);
    setTimeout(() => { if (el) { el.focus(); el.setSelectionRange(caret, caret); } }, 0);
  }
  const formulaProps = (kind, index, key = "formula") => ({
    onFocus: (e) => { focused.current = { kind, index, key, el: e.target }; },
  });

  const outputs = result?.columns || [];
  const numeric = result?.numeric || [];
  const preview = toWidget(result, view, title || "Custom widget");

  return (
    // No close on a backdrop click: a stray click outside would throw away a half-built query. ✕ and Cancel close it.
    <div className="modal-backdrop">
      <div className="form-modal wb-modal">
        <div className="form-modal-head">
          <div className="form-modal-title">{editing ? "Edit widget" : "Create a widget"}</div>
          <button className="icon-btn" onClick={onClose} title="Close">✕</button>
        </div>

        <div className="wb-body">
          <div className="wb-tabs">
            {[["build", "Build"], ["sql", "SQL"], ["prompt", "Ask AI"]].map(([k, l]) => (
              <button key={k} className={"wb-tab" + (tab === k ? " on" : "")} onClick={() => setTab(k)}>{l}</button>
            ))}
            <span className="wb-tab-hint">
              {tab === "build" && "Pick a table, group it, and write measures as formulas."}
              {tab === "sql" && "Write a SELECT. Use {{date_from}} and {{date_to}} to follow the dashboard's date range."}
              {tab === "prompt" && "Describe the widget; the AI writes the SQL, which you can edit before running."}
            </span>
          </div>

          {error && <Alert variant="danger" className="wb-alert" dismissible onClose={() => setError("")}>{error}</Alert>}

          {tab === "build" && (
            datasets === null ? <Spinner animation="border" size="sm" /> : (
              <div className="wb-build">
                <div className="wb-main">
                  <label className="wb-label">Table</label>
                  <select className="wb-input" value={build.dataset} title={ds?.description || ""}
                    onChange={(e) => setBuild(emptyBuild(e.target.value))}>
                    {datasets.map((d) => <option key={d.key} value={d.key} title={d.description || ""}>{d.key}</option>)}
                  </select>
                  {ds?.description && <div className="wb-desc wb-desc-table" title={ds.description}>{ds.description}</div>}

                  {(build.joins || []).length > 0 && (
                    <label className="wb-label">Joined tables <span className="wb-sub">use their columns as [B.Column]; a table with several rows per key is totalled on its own first, so nothing is double-counted</span></label>
                  )}
                  {(build.joins || []).map((j, i) => {
                    const alias = ALIASES[i];
                    const jds = findDs(j.dataset);
                    const jdates = (jds?.columns || []).filter((c) => c.kind === "date").map((c) => c.name);
                    const setJoin = (patch) => set({ joins: build.joins.map((x, k) => (k === i ? { ...x, ...patch } : x)) });
                    return (
                      <div className="wb-join" key={`j${i}`}>
                        <div className="wb-row">
                          <span className="wb-alias">{alias}</span>
                          <select className="wb-input" value={j.dataset} title={jds?.description || ""}
                            onChange={(e) => setJoin({ dataset: e.target.value, on: [{ left: "", right: "" }], date_field: "" })}>
                            <option value="">— table —</option>
                            {datasets.map((d) => <option key={d.key} value={d.key} title={d.description || ""}>{d.key}</option>)}
                          </select>
                          <select className="wb-input wb-narrow" value={j.type || "left"} onChange={(e) => setJoin({ type: e.target.value })}>
                            {JOIN_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                          </select>
                          <button className="dt-icon-btn danger" title="Remove this join"
                            onClick={() => set({ joins: build.joins.filter((_, k) => k !== i) })}>✕</button>
                        </div>
                        {(j.on || []).map((pair, n) => (
                          <div className="wb-row wb-join-on" key={`on${n}`}>
                            <span className="wb-sub">match</span>
                            <select className="wb-input" value={pair.left} onChange={(e) => setJoin({ on: j.on.map((x, k) => (k === n ? { ...x, left: e.target.value } : x)) })}>
                              <option value="">— {build.dataset} column —</option>
                              {(ds?.columns || []).map((c) => <option key={c.name} value={c.name} title={c.description || ""}>{c.name}</option>)}
                            </select>
                            <span className="wb-eq">=</span>
                            <select className="wb-input" value={pair.right} onChange={(e) => setJoin({ on: j.on.map((x, k) => (k === n ? { ...x, right: e.target.value } : x)) })}>
                              <option value="">— {j.dataset || "joined"} column —</option>
                              {(jds?.columns || []).map((c) => <option key={c.name} value={c.name} title={c.description || ""}>{c.name}</option>)}
                            </select>
                            {j.on.length > 1 && <button className="dt-icon-btn danger" onClick={() => setJoin({ on: j.on.filter((_, k) => k !== n) })}>✕</button>}
                          </div>
                        ))}
                        <div className="wb-row wb-join-on">
                          <button className="wb-link" onClick={() => setJoin({ on: [...(j.on || []), { left: "", right: "" }] })}>+ another matching column</button>
                          {jdates.length > 0 && (
                            <label className="wb-label-inline">Date range filters {alias} on
                              <select className="wb-input" value={j.date_field || ""} onChange={(e) => setJoin({ date_field: e.target.value })}>
                                <option value="">{jds?.dateField ? `${jds.dateField} (default)` : "—"}</option>
                                {jdates.map((c) => <option key={c} value={c}>{c}</option>)}
                              </select>
                            </label>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {(build.joins || []).length < ALIASES.length && (
                    <button className="wb-link" onClick={() => set({ joins: [...(build.joins || []), { dataset: "", type: "left", on: [{ left: "", right: "" }], date_field: "" }] })}>
                      + join another table
                    </button>
                  )}

                  <label className="wb-label">Group by <span className="wb-sub">(the labels / x axis; leave empty for one total)</span></label>
                  {build.dimensions.map((d, i) => (
                    <div className="wb-row" key={`d${i}`}>
                      <select className="wb-input" value={d.field} onChange={(e) => setRow("dimensions", i, { field: e.target.value, bucket: "" })}>
                        <option value="">— none —</option>
                        {[...colNames.map(colOption), ...calcNames.map((c) => <option key={c} value={c}>{c}</option>)]}
                      </select>
                      {dateCols.includes(d.field) && (
                        <select className="wb-input wb-narrow" value={d.bucket} onChange={(e) => setRow("dimensions", i, { bucket: e.target.value })}>
                          {BUCKETS.map((b) => <option key={b} value={b}>{b ? `by ${b}` : "exact date"}</option>)}
                        </select>
                      )}
                      {build.dimensions.length > 1 && <button className="dt-icon-btn danger" onClick={() => dropRow("dimensions", i)}>✕</button>}
                      {describe.get(d.field) && <div className="wb-desc wb-desc-row">{describe.get(d.field)}</div>}
                    </div>
                  ))}
                  {build.dimensions.length < 2 && (
                    <button className="wb-link" onClick={() => addRow("dimensions", { field: "", bucket: "" })}>+ second grouping (split into series)</button>
                  )}

                  <label className="wb-label">Measures <span className="wb-sub">e.g. SUM([Invc Amt]) / 1e7 &nbsp;·&nbsp; SUM([Collected]) / SUM([Billed]) * 100</span></label>
                  {build.measures.map((m, i) => (
                    <div className="wb-row" key={`m${i}`}>
                      <input className="wb-input wb-narrow" placeholder="Name" value={m.label} onChange={(e) => setRow("measures", i, { label: e.target.value })} />
                      <input className="wb-input wb-formula" placeholder="SUM([Column])" value={m.formula}
                        onChange={(e) => setRow("measures", i, { formula: e.target.value })} {...formulaProps("measures", i)} />
                      {build.measures.length > 1 && <button className="dt-icon-btn danger" onClick={() => dropRow("measures", i)}>✕</button>}
                    </div>
                  ))}
                  <button className="wb-link" onClick={() => addRow("measures", { label: "", formula: "" })}>+ measure</button>

                  <label className="wb-label">Calculated columns <span className="wb-sub">row-level, e.g. [Amount] * [Rate] or IF([Status] = 'Open', [Amount], 0) — use them like any column</span></label>
                  {(build.calculated || []).map((c, i) => (
                    <div className="wb-row" key={`c${i}`}>
                      <input className="wb-input wb-narrow" placeholder="Name" value={c.name} onChange={(e) => setRow("calculated", i, { name: e.target.value })} />
                      <input className="wb-input wb-formula" placeholder="[Column A] - [Column B]" value={c.formula}
                        onChange={(e) => setRow("calculated", i, { formula: e.target.value })} {...formulaProps("calculated", i)} />
                      <button className="dt-icon-btn danger" onClick={() => dropRow("calculated", i)}>✕</button>
                    </div>
                  ))}
                  <button className="wb-link" onClick={() => addRow("calculated", { name: "", formula: "" })}>+ calculated column</button>

                  <label className="wb-label">Filters</label>
                  {(build.filters || []).map((f, i) => (
                    <div className="wb-row" key={`f${i}`}>
                      <select className="wb-input" value={f.field} onChange={(e) => setRow("filters", i, { field: e.target.value })}>
                        <option value="">— column —</option>
                        {colNames.map(colOption)}
                      </select>
                      <select className="wb-input wb-narrow" value={f.op} onChange={(e) => setRow("filters", i, { op: e.target.value })}>
                        {OPS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                      </select>
                      {!NO_VALUE_OPS.has(f.op) && (
                        <input className="wb-input" placeholder={f.op === "in" || f.op === "nin" ? "a, b, c" : dateCols.includes(f.field) ? "YYYY-MM-DD" : "value"}
                          value={f.value} onChange={(e) => setRow("filters", i, { value: e.target.value })} />
                      )}
                      {f.op === "between" && (
                        <input className="wb-input" placeholder="to" value={f.value2 || ""} onChange={(e) => setRow("filters", i, { value2: e.target.value })} />
                      )}
                      <button className="dt-icon-btn danger" onClick={() => dropRow("filters", i)}>✕</button>
                    </div>
                  ))}
                  {(build.conditions || []).map((c, i) => (
                    <div className="wb-row" key={`k${i}`}>
                      <input className="wb-input wb-formula" placeholder="[Billed] > [Collected] AND [Status] != 'Closed'" value={c}
                        onChange={(e) => set({ conditions: build.conditions.map((x, j) => (j === i ? e.target.value : x)) })} />
                      <button className="dt-icon-btn danger" onClick={() => set({ conditions: build.conditions.filter((_, j) => j !== i) })}>✕</button>
                    </div>
                  ))}
                  <span>
                    <button className="wb-link" onClick={() => addRow("filters", { field: "", op: "eq", value: "" })}>+ filter</button>
                    <button className="wb-link" onClick={() => set({ conditions: [...(build.conditions || []), ""] })}>+ condition (formula)</button>
                  </span>

                  <div className="wb-row wb-options">
                    <label className="wb-label-inline">Dashboard date range filters
                      <select className="wb-input" value={build.date_field} onChange={(e) => set({ date_field: e.target.value })}>
                        <option value="">{ds?.dateField ? `${ds.dateField} (default)` : "— no date column —"}</option>
                        {mainDateCols.map(colOption)}
                      </select>
                    </label>
                    <label className="wb-label-inline">Sort by
                      <input className="wb-input wb-narrow" placeholder="output name" value={build.sort?.by || ""}
                        onChange={(e) => set({ sort: { ...build.sort, by: e.target.value } })} />
                      <select className="wb-input wb-narrow" value={build.sort?.desc === false ? "asc" : "desc"}
                        onChange={(e) => set({ sort: { ...build.sort, desc: e.target.value === "desc" } })}>
                        <option value="desc">high → low</option><option value="asc">low → high</option>
                      </select>
                    </label>
                    <label className="wb-label-inline">Rows
                      <input className="wb-input wb-tiny" type="number" min="1" max="1000" value={build.limit}
                        onChange={(e) => set({ limit: Number(e.target.value) || 100 })} />
                    </label>
                  </div>
                </div>

                <div className="wb-side">
                  <div className="wb-side-head">Insert into formula</div>
                  <div className="wb-ops">
                    {INSERTS.map((s) => <button key={s} className="wb-chip op" onClick={() => insert(s.includes("(") || s.length > 1 ? s : ` ${s} `)}>{s}</button>)}
                  </div>
                  <div className="wb-side-head">Columns</div>
                  <div className={"wb-colinfo" + (hovered ? " on" : "")}>
                    {hovered ? (
                      <>
                        <div className="wb-colinfo-name">{hovered.name} <span className="wb-colinfo-kind">{hovered.kind}</span></div>
                        <div className="wb-colinfo-desc">{hovered.description || "No description in the Table Catalog yet."}</div>
                      </>
                    ) : (
                      <div className="wb-colinfo-desc">Hover a column to see what it means. Click it to insert it into a formula.</div>
                    )}
                  </div>
                  <div className="wb-cols">
                    {calcNames.map((c) => <button key={`calc-${c}`} className="wb-chip calc" onClick={() => insert(`[${c}]`)} title="Calculated column">{c}</button>)}
                    {tables.map((t) => (
                      <div key={t.alias} className="wb-col-group">
                        {tables.length > 1 && (
                          <div className="wb-col-group-head"><span className="wb-alias">{t.alias}</span>{t.ds.key}</div>
                        )}
                        {(t.ds.columns || []).map((c) => {
                          const ref = t.alias === "A" ? c.name : `${t.alias}.${c.name}`;
                          return (
                            <button key={ref} className={"wb-chip" + (c.description ? " described" : "")} onClick={() => insert(`[${ref}]`)}
                              title={tip(ref)} onMouseEnter={() => setHovered({ ...c, name: ref })} onMouseLeave={() => setHovered(null)}
                              onFocus={() => setHovered({ ...c, name: ref })} onBlur={() => setHovered(null)}>
                              <span className={"wb-kind " + c.kind}>{c.kind === "number" ? "#" : c.kind === "date" ? "📅" : "Aa"}</span>{c.name}
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )
          )}

          {tab === "prompt" && (
            <div className="wb-prompt">
              <textarea className="wb-input wb-textarea" rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g. Monthly billed vs collected in crores for Rentco, following the dashboard date range" />
              <button className="dash-btn primary" onClick={generate} disabled={busy !== "" || !prompt.trim()}>
                {busy === "generate" ? "Writing SQL…" : "✦ Generate SQL"}
              </button>
            </div>
          )}

          {(tab === "sql" || (tab === "prompt" && sql)) && (
            <textarea className="wb-input wb-textarea wb-sql" rows={tab === "sql" ? 9 : 7} spellCheck={false} value={sql}
              onChange={(e) => setSql(e.target.value)}
              placeholder={'SELECT "Customer Name", SUM("Invc Amt") / 1e7 AS "Billed (Cr)"\nFROM "Rentco_Billed_Report"\nWHERE "Invc Posting Date" BETWEEN {{date_from}} AND {{date_to}}\nGROUP BY 1 ORDER BY 2 DESC LIMIT 10'} />
          )}

          <div className="wb-runbar">
            <button className="dash-btn primary" onClick={run} disabled={busy !== "" || (tab !== "build" && !sql.trim())}>
              {busy === "run" ? "Running…" : "▶ Run"}
            </button>
            {result?.joins?.length > 0 && (
              <span className="filter-note">
                {result.joins.map((j) => `${j.alias}: ${j.kind === "lookup" ? "one row per key — joined row by row" : "several rows per key — totalled first"}`).join(" · ")}
              </span>
            )}
            {result && <span className="filter-note">{result.returned} row{result.returned === 1 ? "" : "s"}{dateRange?.dateFrom || dateRange?.dateTo ? " · in the dashboard's date range" : ""}{result.followsDates === false ? " · this SQL won't follow the dashboard date range" : ""}</span>}
          </div>

          {result && (
            <div className="wb-result">
              <div className="wb-view">
                <input className="wb-input wb-title" placeholder="Widget title" value={title} onChange={(e) => setTitle(e.target.value)} />
                <div className="wb-types">
                  {TYPES.map(([k, l]) => (
                    <button key={k} className={"wb-chip" + (view.type === k ? " on" : "")} onClick={() => setView((v) => ({ ...v, type: k }))}>{l}</button>
                  ))}
                </div>
                {view.type !== "table" && view.type !== "kpi" && (
                  <label className="wb-label-inline">Labels (x)
                    <select className="wb-input" value={view.x} onChange={(e) => setView((v) => ({ ...v, x: e.target.value }))}>
                      <option value="">—</option>
                      {outputs.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </label>
                )}
                {view.type !== "table" && (
                  <div className="wb-ys">
                    <span className="wb-label-inline">Values</span>
                    {numeric.map((c) => (
                      <label key={c} className="wb-check">
                        <input type="checkbox" checked={(view.y || []).includes(c)}
                          onChange={(e) => setView((v) => ({ ...v, y: e.target.checked ? [...(v.y || []), c] : (v.y || []).filter((x) => x !== c) }))} />
                        {c}
                      </label>
                    ))}
                    {!numeric.length && <span className="wb-sub">No numeric column in the result.</span>}
                  </div>
                )}
                {(view.type === "bar" || view.type === "line") && (
                  <label className="wb-label-inline">Split into series by
                    <select className="wb-input" value={view.split || ""} onChange={(e) => setView((v) => ({ ...v, split: e.target.value }))}>
                      <option value="">— none —</option>
                      {outputs.filter((c) => c !== view.x && !numeric.includes(c)).map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </label>
                )}
              </div>
              <div className="wb-preview"><Preview widget={preview} /></div>
              <details className="wb-sqlview">
                <summary>Data ({result.returned} rows) and SQL</summary>
                <TableWidget spec={{ columns: result.columns, rows: result.rows.slice(0, 50), matched_rows: result.returned, returned: Math.min(50, result.returned) }} />
                <pre>{result.sql}</pre>
              </details>
            </div>
          )}
        </div>

        <div className="form-modal-foot">
          <button className="dash-btn subtle" onClick={onClose}>Cancel</button>
          <button className="dash-btn primary" onClick={save} disabled={busy !== "" || !result || !preview}>
            {busy === "save" ? "Saving…" : editing ? "Save changes" : "Add to dashboard"}
          </button>
        </div>
      </div>
    </div>
  );
}
